import style from "./rodinpreviewboard.module.css";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import QRCode from "qrcode";
// import { IoMdDownload } from "react-icons/io";
import { AvatarImage } from "../../../common/AvatarImage";
import {
  logInfoAtom,
  rodinGenPipeline,
  taskDetailAtom,
  renderPreviewImgAtom,
  showLoginAtom,
  cardsAtom,
  cardsTypeAtom,
  showGoActBrand,
  rodinRenderAtom,
} from "../../../store";
import { useCallback, useEffect, useRef, useState } from "react";
import { AiFillCheckCircle, AiFillHeart } from "react-icons/ai";
import { FaLink } from "react-icons/fa";
import HideCard from "./HideIcon";
import Private from "../../widgets/Private";
import Switch from "./assets/switch.png";
import { ImageOverlay } from "../../../common/ImageOverlay";
import { likeCard } from "../../../utils/net";
import { trackEvent } from '../../../utils/trackEvent'
import { checkMobile, url2Base64 } from "../../../utils/format";
// import { useTips } from "../../../common/GlobalTips";
import * as Sentry from "@sentry/react";
import { isPrerenderUserAgent, useThrottle } from "../../../utils/hooks";
import { RodinPoster } from "../../../common/SharePoster";
import { useTranslation } from "react-i18next";
import Restricted from "../../../utils/restricted";
// import { RodinPoster } from '../../../common/SharePoster';
import CloseIcon from "./assets/close.svg";
import { ErrorPage } from "../../ErrorPage";

const STAGE_ENUM = {
  IMAGE_PREVIEW: "IMAGE_PREVIEW",
  MODEL_PREVIEW: "MODEL_PREVIEW",
  RAW_PREVIEW: "RAW_PREVIEW",
  MTL_PREVIEW: "MTL_PREVIEW",
  GPU_PREVIEW: "GPU_PREVIEW",
};

const RodinPreviewBoard = () => {
  let taskDetail = useRecoilValue(taskDetailAtom);
  const [currentPreviewType, setCurrentPreviewType] = useState("black");
  const dynamicClassName = style[currentPreviewType];
  const bodyRef = useRef(null);
  const logInfo = useRecoilValue(logInfoAtom);
  // const [isLoading, setIsLoading] = useState(true)
  const [currentStage, setCurrentStage] = useState(STAGE_ENUM.IMAGE_PREVIEW);
  const setPreviewImage = useSetRecoilState(renderPreviewImgAtom);
  const [modelLoadRequest, setModelLoadRequest] = useState({
    geo: false,
    mtl: false,
  });
  const [currentViewMode, setViewMode] = useState(
    localStorage.getItem("rodinRenderMode") || "Shaded"
  );

  const setCurrentViewMode = useCallback((prop) => {
    // Tracer
    if (['Shaded', 'PBR', 'Toon'].includes(prop)) {
      localStorage.setItem("rodinRenderMode", prop);
    }
    setViewMode(prop);
  });
  const [genPipeline] = useRecoilState(rodinGenPipeline);
  const [shareOpen, setShareOpen] = useState(false);

  const defaultTaskDetail = {
    author: {
      user_uuid: localStorage.getItem("user_uuid"),
      avatar_url: logInfo?.avatar_url,
      username: logInfo?.username,
    },
    task_uuid: genPipeline.taskInfo.data,
  };

  useEffect(() => {
    return () => {
      setPreviewImage(false);
    };
  }, []);

  useEffect(() => {
    if (genPipeline.geoPreviewLoading.status) {
      if (genPipeline.geoPreviewLoading.data) {
        setPreviewImage(genPipeline.geoPreviewLoading.data);
      }
    }

    // If both geo and mtl are provided, then we can proceed both all together
    if (genPipeline.geoLoading.status && genPipeline.mtlLoading.status) {
      setModelLoadRequest({
        geo: genPipeline.geoLoading.data,
        mtl: genPipeline.mtlLoading.data,
      });
      // setCurrentViewMode("")
    } else if (genPipeline.geoLoading.status) {
      if (genPipeline.geoLoading.data) {
        if (!["Geometry", "Wireframe"].includes(currentViewMode)) {
          if (taskDetail.settings["mesh_export_type"] === "Raw") {
            setCurrentViewMode("Raw");
          } else {
            setCurrentViewMode("Geometry");
          }
        }
        setModelLoadRequest({ geo: genPipeline.geoLoading.data, mtl: false });
      }
    } else if (genPipeline.mtlLoading.status) {
      if (["Geometry", "Wireframe", "Raw"].includes(currentViewMode)) {
        setCurrentViewMode("Shaded");
      }
      setModelLoadRequest({
        geo: false,
        mtl: genPipeline.mtlLoading.data,
      });
    }

    if (genPipeline.geoConfirmed.status) {
      if (taskDetail.settings["mesh_export_type"] === "Raw") {
        setCurrentStage(STAGE_ENUM.RAW_PREVIEW);
      } else {
        setCurrentStage(STAGE_ENUM.MODEL_PREVIEW);
      }
    }

    if (genPipeline.mtlLoaded.status) {
      if ("gpu" in navigator && /Chrome\/[\d.]+/.test(navigator.userAgent)) {
        navigator.gpu.requestAdapter().then((adapter) => {
          if (adapter) {
              adapter.requestDevice({
                requiredLimits: { maxStorageBufferBindingSize: 2147483644 },
              }).then(() => {
                setCurrentStage(STAGE_ENUM.GPU_PREVIEW);
              }).catch(() => {
                setCurrentStage(STAGE_ENUM.MTL_PREVIEW);
              });
          } else {
            setCurrentStage(STAGE_ENUM.MTL_PREVIEW);
          }
        });
      } else {
        setCurrentStage(STAGE_ENUM.MTL_PREVIEW);
      }
    }
  }, [genPipeline]);

  taskDetail = taskDetail || defaultTaskDetail;

  const isMobile = checkMobile();

  const [loaded, setLoaded] = useState(false);

  const onLoad = useCallback(() => {
    setLoaded(true);
  }, []);

  const [posterInfo, setPosterInfo] = useState({
    qrInfo: {
      url: "",
      base64: "",
    },
    previewInfo: {
      url: taskDetail?.rodin_generate_info?.mesh_input_images["0000.png"],
      base64: "",
    },
    prompt: taskDetail.prompt,
    url_arr: [],
  });
  const fetchImg = async () => {
    const qr = `http://hyper3d.ai/rodin/${taskDetail.task_uuid}`;
    QRCode.toDataURL(qr, { margin: 2, width: 200 }, (err, qr) => {
      if (err) return console.error(err);

      url2Base64(qr).then((qrBase64) => {
        setPosterInfo((pre) => {
          return {
            ...pre,
            qrInfo: {
              url: qr,
              base64: qrBase64,
            },
          };
        });
      });
      const card_poster_info = taskDetail?.card_poster_info;
      const oldUrls = [...posterInfo.url_arr];
      // const keys = Object.keys(card_poster_info).filter((i) => i !== 'render_video')
      if (taskDetail.step === "ModelGenerate") {
        url2Base64(taskDetail.image_url).then((base64) => {
          setPosterInfo((pre) => {
            return {
              ...pre,
              url_arr: [
                {
                  type: "preview",
                  url: taskDetail.image_url,
                  base64: base64,
                },
              ],
            };
          });
        });
      } else {
        const preferredOrder = [
          "material_video",
          "material_image",
          "geometry_video",
          "geometry_image",
          "preview_image",
        ];
        const sortedKeys = preferredOrder.filter(
          (key) => key in card_poster_info
        );
        sortedKeys.map((item) => {
          const tempObj = {
            type: item,
            url: card_poster_info[item],
            base64: "",
          };

          if (tempObj.url !== "FILE_NOT_FOUND") {
            if (
              ["geometry_image", "material_image", "preview_image"].includes(
                item
              )
            ) {
              url2Base64(tempObj.url).then((base64) => {
                tempObj.base64 = base64;
                oldUrls.push(tempObj);
              });
            } else {
              oldUrls.push(tempObj);
            }
            setPosterInfo((pre) => {
              return {
                ...pre,
                url_arr: oldUrls,
              };
            });
          }
        });
      }

      url2Base64(posterInfo.previewInfo.url).then((previewBase64) => {
        setPosterInfo((pre) => {
          return {
            ...pre,
            previewInfo: {
              ...pre.previewInfo,
              base64: previewBase64,
            },
          };
        });
      });
    });
  };

  useEffect(() => {
    fetchImg();
    const existingScript = document.querySelector("#newsArticleLdJson");
    if (existingScript) {
      existingScript.remove();
    }

    const linkedData = document.createElement("script");
    linkedData.type = "application/ld+json";
    linkedData.id = "newsArticleLdJson";
    linkedData.text = `{
      "@context": "https://schema.org",
      "@type": "MediaObject",
      "headline": "${taskDetail.prompt.replaceAll('"', '')}",
      "contentUrl": "${taskDetail.image_url}",
      "image": ["${taskDetail.image_url}"],
      "datePublished": "${taskDetail.time}",
      "author": [{
          "@type": "Person",
          "name": "${taskDetail.author.username}"
        }]
    }`;
    document.head.appendChild(linkedData);
  }, [taskDetail]);

  return (
    <div
      className={`${style.container} ${isMobile ? style.mobileContainer : ""}`}
    >
      <div className={style.header}>
        <CreatorInfo taskDetail={taskDetail} />
        <Restricted to="share.card.rodin.view">
          <ShareBtn
            taskDetail={taskDetail}
            setShareOpen={setShareOpen}
          />
        </Restricted>
        <Restricted to="private.card.rodin.view">
          <HideCard />
        </Restricted>
        <Restricted to="like.card.rodin.view">
          <LikeBtn taskDetail={taskDetail} />
        </Restricted>
        {shareOpen && (
          <RodinPoster
            posterInfo={posterInfo}
            setShareOpen={setShareOpen}
            shareOpen={shareOpen}
          />
        )}
        {isPrerenderUserAgent() &&
          posterInfo.url_arr.length &&
          [0, 1].map((currentIndex) =>
            [
              "geometry_image",
              "material_image",
              "preview",
              "preview_image",
            ].includes(posterInfo.url_arr[currentIndex]?.type) ||
            posterInfo.url_arr.length === 1 ? (
              <img
                key={currentIndex}
                alt="poster"
                src={posterInfo?.url_arr[currentIndex]?.base64}
              />
            ) : (
              <video
                key={currentIndex}
                src={posterInfo?.url_arr[currentIndex]?.url}
              />
            )
          )}
        <Restricted to="private.card.rodin.view">
          {taskDetail?.author?.user_uuid ===
            localStorage.getItem("user_uuid") && (
            <div className={style.titleBtn}>
              <Private
                card={{ ...taskDetail }}
                type="detail"
              />
            </div>
          )}
        </Restricted>
      </div>
      <div
        className={style.body}
        ref={bodyRef}
      >
        <ViewSwitch
          setCurrentViewMode={setCurrentViewMode}
          currentStage={currentStage}
        />
        <div className={`${style.gradient} ${dynamicClassName}`}></div>
        {/* {loaded && ["Tracer"].includes(currentViewMode) && <Brander />} */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex:
              currentStage !== STAGE_ENUM.IMAGE_PREVIEW &&
              currentViewMode !== "Preview"
                ? 1000
                : -1,
            visibility:
              currentStage !== STAGE_ENUM.IMAGE_PREVIEW &&
              currentViewMode !== "Preview"
                ? "visible"
                : "hidden",
          }}
        >
          <ModelPreview
            onLoad={onLoad}
            shareOpen={shareOpen}
            currentStage={currentStage}
            setShareOpen={setShareOpen}
            loadRequest={modelLoadRequest}
            currentViewMode={currentViewMode}
            setCurrentViewMode={setCurrentViewMode}
          />
        </div>
        {(currentStage === STAGE_ENUM.IMAGE_PREVIEW ||
          currentViewMode === "Preview") && (
          <ImageGrid
            currentPreviewType={currentPreviewType}
            setCurrentPreviewType={setCurrentPreviewType}
          />
        )}
      </div>
    </div>
  );
};

const Brander = () => {
  const [open, setOpen] = useRecoilState(showGoActBrand);
  return (
    <div className={`${style.Brand} ${open ? "" : style.close}`}>
      <div
        className={`${style.brandTitle}`}
        onClick={() => {
          window.open("https://www.goact.fun", "_target");
        }}
      >
        Rendered By GoAct
      </div>
      <div className={style.border}></div>
      <div
        className={`${style.closeIcon}`}
        onClick={() => {
          setOpen(false);
        }}
      >
        <img
          src={CloseIcon}
          alt="close"
        />
      </div>
    </div>
  );
};

const ViewSwitch = ({ setCurrentViewMode, currentStage }) => {
  const { t } = useTranslation();
  const taskDetail = useRecoilValue(taskDetailAtom);
  const [stageList, setStageList] = useState([
    {
      name: t("RODIN_GENERTAE_PRE_BOARD_PREVIEW"),
      symbol: "Preview",
    },
  ]);

  useEffect(() => {
    const updateStageList = (newStages) => {
      setStageList((prev) => {
        const existingStages = new Set(prev.map((stage) => stage.name));
        const stagesToAdd = newStages.filter(
          (stage) => !existingStages.has(stage.name)
        );

        return stagesToAdd.length > 0 ? [...prev, ...stagesToAdd] : prev;
      });
    };

    if (currentStage === STAGE_ENUM.RAW_PREVIEW) {
      updateStageList([
        { name: t("RODIN_GENERTAE_PRE_BOARD_GEOMETRY"), symbol: "Raw" },
      ]);
    }
    if (currentStage === STAGE_ENUM.MODEL_PREVIEW) {
      updateStageList([
        { name: t("RODIN_GENERTAE_PRE_BOARD_GEOMETRY"), symbol: "Geometry" },
        { name: t("RODIN_GENERTAE_PRE_BOARD_WIREFRAME"), symbol: "Wireframe" },
      ]);
    } else if (currentStage === STAGE_ENUM.MTL_PREVIEW) {
      updateStageList([
        { name: t("RODIN_GENERTAE_PRE_BOARD_PBR"), symbol: "PBR" },
        { name: t("RODIN_GENERTAE_PRE_BOARD_SHADED"), symbol: "Shaded" },
        { name: t("RODIN_GENERTAE_PRE_BOARD_TOON"), symbol: "Toon" },
      ]);
    } else if (currentStage === STAGE_ENUM.GPU_PREVIEW) {
      updateStageList([
        { name: t("RODIN_GENERTAE_PRE_BOARD_PBR"), symbol: "PBR" },
        { name: t("RODIN_GENERTAE_PRE_BOARD_SHADED"), symbol: "Shaded" },
        { name: t("RODIN_GENERTAE_PRE_BOARD_TOON"), symbol: "Toon" },
        // { name: "Tracer", symbol: "Tracer" },
      ]);
    }
  }, [currentStage]);

  const handleModeChange = (name) => {
    if (name && currentStage !== STAGE_ENUM.IMAGE_PREVIEW) {
      trackEvent('Rodin - Model - Viewer: Change', { currentViewer:name }, taskDetail?.task_uuid)
      setCurrentViewMode(name);
    }
  };

  // if (stageList.length < 2) {
  //   return null
  // }

  return (
    <div className={`${style.hoverListWrapper}`}>
      <div className={`${style.Icon}`}>
        <img
          src={Switch}
          alt="viewSwitch"
        />
      </div>
      <ul className={`${style.hoverList}`}>
        {stageList.slice(0, stageList.length).map((item, index) => {
          return (
            <li
              onClick={handleModeChange.bind(null, item.symbol)}
              className={`${style.hoverpluginItem} `}
              key={index}
            >
              {item.name}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const CreatorInfo = () => {
  const logInfo = useRecoilValue(logInfoAtom);
  const taskDetail = useRecoilValue(taskDetailAtom);
  return (
    <div className={style.creatorBox}>
      <div className={style.avatar}>
        {taskDetail ? (
          <AvatarImage url={taskDetail?.author?.avatar_url} />
        ) : null}
      </div>
      <div className={style.creatorInfoCon}>
        <div className={style.creatorName}>{taskDetail?.author?.username}</div>
        <div className={style.creatorInfo}>
          {logInfo.is_admin ? taskDetail?.author?.email : null}
        </div>
      </div>
    </div>
  );
};

const ShareBtn = ({ setShareOpen }) => {
  const taskDetail = useRecoilValue(taskDetailAtom);
  const [isShared, setIsShared] = useState(false);
  const { t } = useTranslation();

  const handleShare = async () => {
    try {
      trackEvent('Rodin - Share: Open', false, taskDetail?.task_uuid)
      const url = `${window.location.origin}/rodin/${taskDetail.task_uuid}`;
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
      } else {
        const textarea = document.createElement("textarea");
        if (document.body) {
          document.body.appendChild(textarea);
          textarea.style.position = "fixed";
          textarea.zIndex = -999;
          textarea.style.top = "10px";
          textarea.value = url;
          textarea.select();
          document.execCommand("copy", true);
          document.body.removeChild(textarea);
        }
      }
      setIsShared(true);
      setShareOpen(true);
      setTimeout(() => {
        setIsShared(false);
      }, 1500);
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  return (
    <div style={{ position: "relative" }}>
      {isShared ? (
        <div
          className={`${style.shareCon} ${
            isShared ? style.shared : style.unshared
          }`}
        >
          <AiFillCheckCircle style={{ color: "green", fontSize: "22px" }} />
          <div
            className={`${style.hideOnSmallScreen}`}
            style={{ width: "71px" }}
          >
            &nbsp;&nbsp;{t("BUTTON_COPIED")}
          </div>
          <div></div>
        </div>
      ) : (
        <div
          className={style.shareCon}
          onClick={handleShare}
        >
          <FaLink style={{ fontSize: "22px", color: "#d5d5d5" }} />
          <div
            className={`${style.hideOnSmallScreen}`}
            style={{ width: "71px" }}
          >
            &nbsp;{t("BUTTON_SHARE")}&nbsp;
          </div>
        </div>
      )}
    </div>
  );
};

const LikeBtn = () => {
  const [taskDetail, setTaskDetail] = useRecoilState(taskDetailAtom);
  const [cards, setCards] = useRecoilState(cardsAtom);
  const setShowLogin = useSetRecoilState(showLoginAtom);
  const logInfo = useRecoilValue(logInfoAtom);
  const { task_uuid, num_like, is_like } = taskDetail;
  const cardsType = useRecoilValue(cardsTypeAtom);

  const updateCards = () => {
    const newCards = JSON.parse(JSON.stringify(cards));
    const index = cards.findIndex((item) => item.task_uuid === task_uuid);
    if (index === -1) return;
    newCards[index].is_like = !is_like;
    newCards[index].num_like = is_like ? num_like - 1 : num_like + 1;
    setCards(newCards);
  };

  const handleLikeClick = async (task_uuid) => {
    _czc.push(["_trackEvent", "Index", "Like Icon", "Gallery Component"]);
    if (!logInfo) {
      setShowLogin(true);
      return;
    }
    try {
      if (["Mine", "Group"].includes(cardsType)) {
        updateCards();
      }
      setTaskDetail((pre) => {
        return {
          ...pre,
          is_like: !pre.is_like,
          num_like: pre.num_like + (pre.is_like ? -1 : 1),
        };
      });
      likeCard({ task_uuid, operation: is_like ? "Delike" : "Like" });
    } catch (e) {
      Sentry.captureException(e);
      console.error(e);
    }
  };

  return (
    <div
      className={`${style.titleBtn} ${is_like ? style.like : style.unLike} `}
      onPointerDown={handleLikeClick.bind(null, task_uuid)}
    >
      <AiFillHeart
        style={{
          transition: "color 0.3s ease",
          color: is_like ? "#fa2d53" : "rgb(206,206,206)",
          fontSize: "20px",
        }}
      />
    </div>
  );
};

const ModelPreview = ({ loadRequest, currentViewMode, onLoad, currentStage }) => {
  const [_, setGenPipeline] = useRecoilState(rodinGenPipeline);
  const RodinRender = useRecoilValue(rodinRenderAtom);

  useEffect(() => {
    if (loadRequest.mtl !== false) {
      setGenPipeline((pre) => {
        return {
          ...pre,
          geoLoading: {
            status: false,
            data: null,
            timestamp: Date.now(),
          },
          geoLoaded: {
            status: true,
            data: null,
            timestamp: Date.now(),
          },
          mtlLoading: {
            status: false,
            data: null,
            timestamp: Date.now(),
          },
          mtlLoaded: {
            status: true,
            data: null,
            timestamp: Date.now(),
          },
        };
      });
    } else {
      setGenPipeline((pre) => {
        return {
          ...pre,
          geoLoading: {
            status: false,
            data: null,
            timestamp: Date.now(),
          },
          geoLoaded: {
            status: true,
            data: null,
            timestamp: Date.now(),
          },
        };
      });
    }
  }, [loadRequest.mtl, loadRequest.geo]);
  const { texture_diffuse, texture_pbr, texture_normal, texture_shaded } =
    loadRequest.mtl || {};

  if (!RodinRender) {
    return null;
  }

  return (
    <div className={style.modelContainer}>
      <Sentry.ErrorBoundary
        fallback={({ error, resetError }) => (
          <ErrorPage
            errorMessage={error?.message}
            resetError={resetError}
          />
        )}
      >
        <RodinRender.App
          onLoad={onLoad}
          currentStage={currentStage}
          model={loadRequest.geo || ""}
          texture_diffuse={texture_diffuse}
          texture_normal={texture_normal}
          texture_pbr={texture_pbr}
          texture_shaded={texture_shaded}
          renderMode={currentViewMode}
        />
      </Sentry.ErrorBoundary>
    </div>
  );
};

// const ImgAnimationPreview = () => {
//   // const uploadImgBlob = useRecoilValue(rodinUploadedImages);
//   const genPipeline = useRecoilValue(rodinGenPipeline);
//   const [boxes, setBoxes] = useState([]);
//   const [pngArr, setPngArr] = useState([])

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const fetchPngArr = await getTaskDetail(genPipeline.taskInfo.data)
//         const result = fetchPngArr?.data?.rodin_generate_info?.mesh_input_images
//         if (result) {
//           setPngArr(Object.values(result))
//         }
//         console.log(fetchPngArr, 'fetchPngArr');
//       } catch (error) {
//         console.log(error);
//       }
//     }
//     fetchData()
//   }, [])

//   useEffect(() => {
//     const positionsByCount = {
//       1: ['center'],
//       2: ['top-left', 'center'],
//       3: ['top-left', 'top-right', 'center'],
//       4: ['top-left', 'top-right', 'bottom-right', 'center'],
//       5: ['top-left', 'top-right', 'bottom-left', 'bottom-right', 'center']
//     };

//     const positionsToUse = positionsByCount[pngArr.length] || positionsByCount[5];

//     const visibleBoxes = pngArr.map((img, index) => ({
//       id: index + 1,
//       position: positionsToUse[index],
//       zIndex: positionsToUse[index] === 'center' ? 1 : 0,
//       backgroundColor: '#D8E3FF',
//       img: img
//     })).filter((box) => positionsToUse.includes(box.position));

//     setBoxes(visibleBoxes);
//   }, [pngArr]);

//   const handleSelect = (cid) => {
//     setBoxes(prevBoxes => {
//       const updatedBoxes = [...prevBoxes];
//       const clickIndex = updatedBoxes.findIndex((item) => item.id === cid);
//       const { position: clickPosition, zIndex: clickZIndex } = updatedBoxes[clickIndex];
//       const centerIndex = updatedBoxes.findIndex((item) => item.position === 'center');
//       updatedBoxes[clickIndex] = { ...updatedBoxes[clickIndex], position: 'center', zIndex: 1 };
//       updatedBoxes[centerIndex] = { ...updatedBoxes[centerIndex], position: clickPosition, zIndex: clickZIndex };
//       return updatedBoxes;
//     });
//   };

//   let containerStyle = {}
//   if (pngArr.length === 2) {
//     containerStyle = { transform: 'translateY(-10px)' };
//   } else if (pngArr.length === 1) {
//     containerStyle = { transform: 'translateY(-30px) translateX(-15px)' };
//   }

//   return (
//     <div className={style.animateContainer} style={containerStyle}>
//       {boxes.map((box, index) => (
//         pngArr.length > index && ( // Only render boxes for which we have images
//           <div
//             key={box.id}
//             className={`${style.box} ${style[box.position]} `}
//             style={{ zIndex: box.zIndex, backgroundColor: box.backgroundColor }}
//             onClick={handleSelect.bind(null, box.id)}
//           >
//             <ImageOverlay src={box.img} alt={`Uploaded content ${box.id}`} />
//             {/* <img src={box.img} alt={`Uploaded content ${box.id}`} /> */}
//           </div>
//         )
//       ))}
//     </div>
//   );
// };

const ImageGrid = ({ currentPreviewType, setCurrentPreviewType }) => {
  const carouselRef = useRef(null);
  const containerRef = useRef(null);
  const tempIndexRef = useRef(null);
  const sliderBar = useRef(null);
  const smSDFWrapperRef = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
  ];
  const smSDFref = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const [startDragging, setStartDragging] = useState(false);
  // const [percentIndex, setPercentIndex] = useState(0);
  const [lastIndex, setLastIndex] = useState(0);
  // const [, setGenPipeline] = useRecoilState(rodinGenPipeline);
  const [previewImage] = useRecoilState(renderPreviewImgAtom);
  const [containerSize, setContainerSize] = useState({
    width: 600,
    height: 300,
  });
  const [smContainerSize, setSmContainerSize] = useState([
    { width: 148, height: 74 },
    { width: 148, height: 74 },
    { width: 148, height: 74 },
    { width: 148, height: 74 },
  ]);

  const SDFInfo = {
    black: {
      bottomBgColor: "#8B8B8B",
      initialPosition: { x: 0, y: 0 },
      sminitialPosition: { x: 0, y: 0 },
    },
    red: {
      bottomBgColor: "#54BAC9",
      initialPosition: { x: -containerSize.width / 2, y: 0 },
      sminitialPosition: { x: -smContainerSize[1].width / 2, y: 0 },
    },
    grey: {
      bottomBgColor: "#91B5D7",
      initialPosition: { x: 0, y: containerSize.width / 2 },
      sminitialPosition: { x: 0, y: smContainerSize[2].width / 2 },
    },
    colorful: {
      bottomBgColor: "#ED994E",
      initialPosition: {
        x: -containerSize.width / 2,
        y: containerSize.width / 2,
      },
      sminitialPosition: {
        x: -smContainerSize[3].width / 2,
        y: smContainerSize[3].width / 2,
      },
    },
  };

  const thumbnailOriginalSize = { width: 720, height: 360 };
  const SDFOriginalSize = { width: 11520, height: 720 };
  const scale = containerSize.height / thumbnailOriginalSize.height;

  const resetContainerSize = async () => {
    // setCurrentPreviewType('black')
    updateSize(carouselRef.current, setContainerSize);
    for (let i = 0; i < smSDFWrapperRef.length; i++) {
      const target = smSDFWrapperRef[i];
      updateSize(target.current, setSmContainerSize, true, i);
    }

    // setGenPipeline(pre => {
    //   return {
    //     ...pre,
    //     geoPreviewLoading: {
    //       status: false,
    //       data: null,
    //       timestamp: Date.now()
    //     },
    //     geoPreviewLoaded: {
    //       status: true,
    //       data: null,
    //       timestamp: Date.now()
    //     }
    //   }
    // });
  };

  const throttleResetContainerSize = useThrottle(resetContainerSize, 0);

  useEffect(() => {
    const areAllRefsMounted = [...smSDFWrapperRef, ...smSDFref].every(
      (ref) => ref.current
    );
    if (previewImage && carouselRef && containerRef && areAllRefsMounted) {
      resetContainerSize();
    }
  }, [
    previewImage,
    carouselRef,
    containerRef,
    ...smSDFWrapperRef,
    ...smSDFref,
  ]);

  useEffect(() => {
    const areAllRefsMounted = [...smSDFWrapperRef, ...smSDFref].every(
      (ref) => ref.current
    );
    if (previewImage && carouselRef && containerRef && areAllRefsMounted) {
      window.addEventListener("resize", throttleResetContainerSize);
    }
    return () => {
      window.removeEventListener("resize", throttleResetContainerSize);
    };
  }, [
    previewImage,
    carouselRef,
    containerRef,
    ...smSDFWrapperRef,
    ...smSDFref,
    currentPreviewType,
  ]);

  const updateSize = (target, func, flag, index) => {
    if (target?.clientWidth && target?.clientHeight) {
      const width = target.clientWidth;
      const height = target.clientHeight;
      let map = ["black", "red", "grey", "colorful"];
      const getPositon = (target, width) => {
        let positonMap = {
          black: { x: 0, y: 0 },
          red: { x: -width / 2, y: 0 },
          grey: { x: 0, y: width / 2 },
          colorful: { x: -width / 2, y: width / 2 },
        };
        return positonMap[target];
      };
      // 注意这个地方不能用 getBoundingClientRect()来获取宽高，这里的小图会经过transform形变，getBoundingClientRect获取的是水平和垂直的位置
      if (flag) {
        func((pre) => {
          return pre.map((item, i) => {
            if (index === i) {
              return { width: 2 * width, height };
            } else {
              return item;
            }
          });
        });
        updatePosition(
          smSDFref[index].current,
          getPositon(map[index], 2 * width)
        );
      } else {
        func({ width: 2 * width, height });
        updatePosition(
          containerRef.current,
          getPositon(currentPreviewType, 2 * width)
        );
      }
    }
  };

  const updatePosition = (target, positionObj) => {
    const { x, y } = positionObj;
    target.style.backgroundPosition = `${x}px  ${y}px`;
  };

  const handlerToggleType = (item) => {
    setCurrentPreviewType(item);
    const { x: preX, y: preY } = SDFInfo[currentPreviewType].initialPosition;
    const { x: nextX, y: nextY } = SDFInfo[item].initialPosition;
    const container = containerRef.current;
    const [x, y] = container.style.backgroundPosition
      .split(" ")
      .map((item) => parseInt(item));
    const positionObj = { x: x + nextX - preX, y: y + nextY - preY };
    updatePosition(containerRef.current, positionObj);
  };

  const handlerMouseMove = (operate, e) => {
    if (operate === "mouse" && e.button !== 0) return;
    if (operate === "touch" && e.touches.length !== 1) return;
    setStartDragging(true);
    const container = containerRef.current;
    const bar = sliderBar.current;
    // const { x, y } = SDFInfo[currentPreviewType].initialPosition
    const [x, y] = container.style.backgroundPosition
      .split(" ")
      .map((item) => parseInt(item));
    const viewWidth = container.offsetWidth;
    const NUMVIEWS = 16;
    const SWITCHDISTANCE = 10;
    const startX = operate === "mouse" ? e.clientX : e.touches[0].clientX;

    const smContainerArr = smSDFref.map((item) => {
      const container = item.current;
      const [x, y] = container.style.backgroundPosition
        .split(" ")
        .map((item) => parseInt(item));
      return { current: container, x, y };
    });
    const handleMove = (e) => {
      const endX = operate === "mouse" ? e.clientX : e.touches[0].clientX;
      let viewIndex = Math.floor((endX - startX) / SWITCHDISTANCE) % NUMVIEWS;
      const backgroundPositionX = viewIndex * viewWidth;
      container.style.backgroundPosition = `${
        x + backgroundPositionX
      }px  ${y}px`;
      for (let i = 0; i < smContainerArr.length; i++) {
        const container = smContainerArr[i];
        const viewWidth = container.current.offsetWidth;
        const backgroundPositionX = viewIndex * viewWidth;
        container.current.style.backgroundPosition = `${
          container.x + backgroundPositionX
        }px  ${container.y}px`;
      }
      // console.log(lastIndex, viewIndex);
      let absIndex = viewIndex > 0 ? viewIndex : viewIndex + 16;
      let finalIndex = (lastIndex + absIndex) % 16;
      bar.style.left = `${(Math.abs(finalIndex) / 16) * 100}%`;

      tempIndexRef.current = finalIndex;
      // setPercentIndex(finalIndex)
    };

    const handleEnd = () => {
      setStartDragging(false);
      setLastIndex(tempIndexRef.current || 0);
      if (operate === "mouse") {
        document.removeEventListener("mousemove", handleMove);
        document.removeEventListener("mouseup", handleEnd);
      } else {
        document.removeEventListener("touchmove", handleMove);
        document.removeEventListener("touchend", handleEnd);
        document.removeEventListener("touchcancel", handleEnd);
      }
    };

    if (operate === "mouse") {
      document.addEventListener("mousemove", handleMove);
      document.addEventListener("mouseup", handleEnd);
    } else {
      document.addEventListener("touchmove", handleMove);
      document.addEventListener("touchend", handleEnd);
      document.addEventListener("touchcancel", handleEnd);
    }

    if (e.preventDefault) {
      e.preventDefault();
    }
  };
  /**
   *  background-size : 11520px * (Wsize / 360)    720px * (Hsize / 360)
   *  black & gray 初始position 0px 0px
   *  red & colorful 初始position container px 0px
   */
  return (
    <>
      <div
        className={style.clickArea}
        onMouseDown={handlerMouseMove.bind(null, "mouse")}
        onTouchStart={handlerMouseMove.bind(null, "touch")}
      >
        <div
          className={`${style.carouselContainer} ${
            startDragging ? style.dragging : ""
          }`}
          ref={carouselRef}
        >
          <div
            className={`${style.sdfPreview} sdfPreview`}
            ref={containerRef}
            style={{
              width: `${containerSize.width}px`,
              height: `${containerSize.height}px`,
              backgroundSize: `${SDFOriginalSize.width * scale}px ${
                SDFOriginalSize.height * scale
              }px`,
              backgroundImage: `url(${previewImage})`,
            }}
          ></div>
          <ImageOverlay
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              top: 0,
              left: 0,
            }}
            alwaysHidden={true}
            src={previewImage}
            alt="Preview Render Image"
          />
        </div>
      </div>
      <div
        className={`${style.categorySelection}  ${
          startDragging ? style.dragging : ""
        }`}
      >
        <div className={style.categoryContent}>
          {["black", "red", "grey", "colorful"].map((item, index) => {
            return (
              <div
                className={`${style.categoryItem} ${
                  currentPreviewType === item ? style.active : ""
                }`}
                onClick={handlerToggleType.bind(null, item)}
                key={item}
                ref={smSDFWrapperRef[index]}
              >
                <div
                  ref={smSDFref[index]}
                  className={`${style.bottomSDFPreview} bottomSDFPreview`}
                  style={{
                    width: `${smContainerSize[index].width}px`,
                    height: `${smContainerSize[index].height}px`,
                    backgroundSize: `${
                      (SDFOriginalSize.width * smContainerSize[index].height) /
                      thumbnailOriginalSize.height
                    }px ${
                      (SDFOriginalSize.height * smContainerSize[index].height) /
                      thumbnailOriginalSize.height
                    }px`,
                    backgroundImage: `url(${previewImage})`,
                    // backgroundPosition: `${SDFInfo[item].sminitialPosition.x}px ${SDFInfo[item].sminitialPosition.y}px`
                  }}
                ></div>
                <ImageOverlay
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    top: 0,
                    left: 0,
                  }}
                  alwaysHidden={true}
                  src={previewImage}
                  alt="Preview Render Image"
                />
              </div>
            );
          })}
        </div>
        <div
          className={style.categoryBottom}
          style={{
            backgroundColor: SDFInfo[currentPreviewType]["bottomBgColor"],
          }}
        ></div>
      </div>
      <div className={`${style.slider} ${startDragging ? style.dragging : ""}`}>
        <div
          ref={sliderBar}
          className={style.sliderBar}
        ></div>
      </div>
    </>
  );
};

export default RodinPreviewBoard;
